import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { ContentContainer } from '../components/HeroSectionStyles';
import ParticlesBackground from '../components/ParticlesBackground';

// Page transition animations
const slideInFromRight = keyframes`
  from { opacity: 0; transform: translateX(40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideOutToLeft = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(-40px); }
`;

const slideOutToRight = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(40px); }
`;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  padding: 20px;
  position: relative;
  overflow: hidden;

  &.enter-right { animation: ${slideInFromRight} 280ms ease-out both; }
  &.enter-left { animation: ${slideInFromLeft} 280ms ease-out both; }
  &.exit-left { animation: ${slideOutToLeft} 240ms ease-in both; }
  &.exit-right { animation: ${slideOutToRight} 240ms ease-in both; }
`;

const Content = styled(ContentContainer)`
  max-width: 1100px;
  width: 92%;
  margin: 0 auto;
  padding: 48px 56px;
  border: 1px solid rgba(100, 182, 255, 0.25);
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.18);
  box-shadow: 0 0 20px rgba(100, 182, 255, 0.08);
  backdrop-filter: blur(8px);
  text-align: left;
`;

const SectionTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.6rem;
  color: #64B6FF;
  margin: 1.2rem 0 1rem;
  text-align: left;
  letter-spacing: 2px;
  text-shadow: 0 0 12px rgba(100, 182, 255, 0.5);
`;

const SectionSubtitle = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.4rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0.5rem 0 1.2rem;
  text-align: left;
  letter-spacing: 1px;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  max-width: 820px;
`;

const Item = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 14px 0;
  font-size: 1.35rem;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.92);
  letter-spacing: 0.5px;

  &::before {
    content: attr(data-idx) '.';
    color: #64B6FF;
    font-weight: 700;
    text-shadow: 0 0 10px rgba(100, 182, 255, 0.6);
    margin-top: 0;
    display: inline-block;
    width: 24px;           /* largura fixa para alinhar o texto */
    flex: 0 0 24px;        /* garante consistência no layout flex */
    text-align: right;     /* alinha o número à direita dessa largura */
  }
`;

const Subline = styled.div`
  font-size: 1.05rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  margin: 6px 0 0 0; /* remove indentação para alinhar com o texto principal */
`;

const LinkAnchor = styled.a`
  color: #64B6FF;
  text-decoration: none;
  border-bottom: 1px dashed rgba(100, 182, 255, 0.35);
  padding-bottom: 1px;
  transition: color 160ms ease, border-color 160ms ease, text-shadow 160ms ease;

  &:hover {
    color: #9cd1ff;
    border-color: rgba(156, 209, 255, 0.6);
    text-shadow: 0 0 8px rgba(100, 182, 255, 0.45);
  }
`;

const BackButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid rgba(100, 182, 255, 0.4);
  background: rgba(10, 18, 28, 0.6);
  color: #64B6FF;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 0 12px rgba(100, 182, 255, 0.25);
  backdrop-filter: blur(4px);
  transition: transform 120ms ease, box-shadow 200ms ease, background 200ms ease;

  &:hover {
    transform: translateX(-2px);
    box-shadow: 0 0 16px rgba(100, 182, 255, 0.45);
    background: rgba(10, 18, 28, 0.75);
  }
`;

const NextButton = styled(BackButton)`
  left: auto;
  right: 20px;

  &:hover {
    transform: translateX(2px);
  }
`;

function ReuniaoOnboardingFormatos() {
  const location = useLocation();
  const navigate = useNavigate();

  const [anim, setAnim] = useState(() => (location.state?.dir === 'back' ? 'enter-left' : 'enter-right'));
  const TOTAL_ITEMS = 4;
  const [visibleCount, setVisibleCount] = useState(1);

  const goBack = () => {
    setAnim('exit-right');
    setTimeout(() => {
      navigate(`/reuniao-onboarding/formatos/video${location.search}`, { state: { dir: 'back' } });
    }, 240);
  };

  const goNext = () => {
    setAnim('exit-left');
    setTimeout(() => {
      navigate(`/reuniao-onboarding/formatos/complexas${location.search}`, { state: { dir: 'forward' } });
    }, 240);
  };

  const revealNext = () => {
    setVisibleCount((c) => {
      if (c < TOTAL_ITEMS) return c + 1;
      // Último item já visível: seguir para Produções complexas
      goNext();
      return c;
    });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') {
        goBack();
      } else if (e.key === 'ArrowRight') {
        revealNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [location.search]);

  return (
    <>
      <ParticlesBackground />
      <PageWrapper className={anim}>
        <BackButton onClick={goBack} aria-label="Voltar">‹</BackButton>
        <NextButton onClick={revealNext} aria-label="Avançar">›</NextButton>
        <Content>
          <SectionTitle>Formatos de Headlines</SectionTitle>
          <SectionSubtitle>Produções simples:</SectionSubtitle>
          <List>
            {visibleCount >= 1 && (
            <Item data-idx="1">
              <div>
                Head estática contendo localização e pontos de interesse
                <Subline>
                  Ref: <LinkAnchor href="https://www.instagram.com/reel/DNq4aHwO47h/" title="https://www.instagram.com/reel/DNq4aHwO47h/" target="_blank" rel="noopener noreferrer">Ver no Instagram</LinkAnchor>
                </Subline>
                <Subline>
                  Ex: <LinkAnchor href="https://drive.google.com/file/d/12OQvnzsWrusG9uryw3eOoSu586lTH1xs/view?usp=drive_link" title="https://drive.google.com/file/d/12OQvnzsWrusG9uryw3eOoSu586lTH1xs/view?usp=drive_link" target="_blank" rel="noopener noreferrer">Abrir no Drive</LinkAnchor>
                </Subline>
              </div>
            </Item>
            )}

            {visibleCount >= 2 && (
            <Item data-idx="2">
              <div>
                Sequencia de imagens seguida do vídeo do imóvel;
                <Subline>
                  Ref: <LinkAnchor href="https://vm.tiktok.com/ZMANcyHtU/" title="https://vm.tiktok.com/ZMANcyHtU/" target="_blank" rel="noopener noreferrer">Ver no TikTok</LinkAnchor>
                </Subline>
                <Subline>
                  Ex: <LinkAnchor href="https://drive.google.com/file/d/1Ef4eVuDGv0zvUOiLBvgFnaVZmYSWU3LM/view?usp=drive_link" title="https://drive.google.com/file/d/1Ef4eVuDGv0zvUOiLBvgFnaVZmYSWU3LM/view?usp=drive_link" target="_blank" rel="noopener noreferrer">Abrir no Drive</LinkAnchor>
                </Subline>
                <Subline>
                  Ex 2: <LinkAnchor href="https://drive.google.com/file/d/1tHNIdau1VNZkdZHOG56u9agXYn-C42sT/view?usp=sharing" title="https://drive.google.com/file/d/1tHNIdau1VNZkdZHOG56u9agXYn-C42sT/view?usp=sharing" target="_blank" rel="noopener noreferrer">Abrir no Drive</LinkAnchor>
                </Subline>
              </div>
            </Item>
            )}

            {visibleCount >= 3 && (
            <Item data-idx="3">
              <div>
                Tela dividida, apresentando os pontos de interesse e vídeo do imóvel;
                <Subline>
                  Ex: <LinkAnchor href="https://drive.google.com/file/d/1fQ-lw8Vwy-CYZSfk-QVfIpAmywiHzf0a/view?usp=drive_link" title="https://drive.google.com/file/d/1fQ-lw8Vwy-CYZSfk-QVfIpAmywiHzf0a/view?usp=drive_link" target="_blank" rel="noopener noreferrer">Abrir no Drive</LinkAnchor>
                </Subline>
              </div>
            </Item>
            )}

            {visibleCount >= 4 && (
            <Item data-idx="4">
              <div>
                Quatro/Três imagens do empreendimento dividindo a tela seguida de vídeo;
                <Subline>
                  Ref: <LinkAnchor href="https://www.instagram.com/reel/DNBUioNSJ43/?igsh=MXh3NTZxbXl6bmN1aQ==" title="https://www.instagram.com/reel/DNBUioNSJ43/?igsh=MXh3NTZxbXl6bmN1aQ==" target="_blank" rel="noopener noreferrer">Ver no Instagram</LinkAnchor>
                </Subline>
                <Subline>
                  Ex 3: <LinkAnchor href="https://drive.google.com/file/d/1ivwMXYw2U7NPkAMgSDFEsm4LsWFIzL0I/view?usp=drive_link" title="https://drive.google.com/file/d/1ivwMXYw2U7NPkAMgSDFEsm4LsWFIzL0I/view?usp=drive_link" target="_blank" rel="noopener noreferrer">Abrir no Drive</LinkAnchor>
                </Subline>
                <Subline>
                  Ex 4: <LinkAnchor href="https://drive.google.com/file/d/1DYmwkG_lk0SY2fGJHgn5ZNz8BijbpHG3/view?usp=drive_link" title="https://drive.google.com/file/d/1DYmwkG_lk0SY2fGJHgn5ZNz8BijbpHG3/view?usp=drive_link" target="_blank" rel="noopener noreferrer">Abrir no Drive</LinkAnchor>
                </Subline>
              </div>
            </Item>
            )}
          </List>
        </Content>
      </PageWrapper>
    </>
  );
}

export default ReuniaoOnboardingFormatos;
